import type { ExplainModel } from './explainCell'

export type ExplainPanelContentProps = {
  model: ExplainModel
  canEdit?: boolean
  reasonDraft?: string
  onReasonDraftChange?: (value: string) => void
  onReasonDraftBlur?: () => void
  onClearOverride?: () => void
}

export function ExplainPanelContent({
  model,
  canEdit = false,
  reasonDraft = '',
  onReasonDraftChange,
  onReasonDraftBlur,
  onClearOverride,
}: ExplainPanelContentProps) {
  return (
    <div className="space-y-3 text-sm">
      <section>
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Computed</p>
        <p className="text-gray-900">{model.computed.formula}</p>
      </section>

      {model.recipe ? (
        <section>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Recipe</p>
          <ul className="space-y-1 text-gray-700">
            <li>Anchor: {model.recipe.anchor}</li>
            {model.recipe.step ? <li>Step: {model.recipe.step}</li> : null}
            {model.recipe.growth ? <li>Growth: {model.recipe.growth}</li> : null}
            {model.recipe.season ? <li>Season: {model.recipe.season}</li> : null}
            {model.recipe.override ? (
              <li>
                <span>Override: {model.recipe.override.summary}</span>
                {canEdit ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      rows={2}
                      placeholder="Reason for override"
                      value={reasonDraft}
                      onChange={(e) => onReasonDraftChange?.(e.target.value)}
                      onBlur={() => onReasonDraftBlur?.()}
                    />
                    <button
                      type="button"
                      className="text-xs text-sky-700 hover:underline"
                      onClick={() => onClearOverride?.()}
                    >
                      Clear override
                    </button>
                  </div>
                ) : model.recipe.override.reason ? (
                  <span className="block text-gray-600">{model.recipe.override.reason}</span>
                ) : null}
              </li>
            ) : null}
          </ul>
        </section>
      ) : null}

      {model.context ? (
        <section>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Context</p>
          <ul className="space-y-1 text-gray-700">
            {model.context.last_actual ? <li>Last actual: {model.context.last_actual}</li> : null}
            {model.context.same_month_last_year ? (
              <li>Same month last year: {model.context.same_month_last_year}</li>
            ) : null}
            {model.context.trailing_12 ? <li>Trailing 12: {model.context.trailing_12}</li> : null}
            {model.context.projected_12 ? <li>Projected 12: {model.context.projected_12}</li> : null}
            {model.context.rank_note ? <li>{model.context.rank_note}</li> : null}
          </ul>
        </section>
      ) : null}

      {model.rationale ? (
        <section>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Rationale</p>
          <p className="text-gray-700">{model.rationale}</p>
        </section>
      ) : null}

      {model.notes.length > 0 ? (
        <section>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Notes</p>
          <ul className="space-y-1 text-gray-700">
            {model.notes.map((note) => (
              <li key={`${note.cell}:${note.text.slice(0, 20)}`}>
                <span className="text-gray-500">{note.cell}:</span> {note.text}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
